import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2, Lock, User } from 'lucide-react';
import type { Beneficiary, BeneficiaryConfig, BeneficiaryKind, BeneficiaryList, BeneficiarySummary, CodeLabel, Impact, ProgramList } from '@mnc/contracts';
import { api } from '../../lib/api';
import { useParameterOptions } from '../../lib/parameters';
import { pick } from '../../i18n';
import { Badge, cn } from '../../components/ui';

export const KINDS: BeneficiaryKind[] = ['organisation', 'individual'];

export function fmtNumber(n: number, locale: string, digits = 2): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(n);
}

export function Label({ value }: { value: CodeLabel | null }) {
  const { i18n } = useTranslation();
  return <>{value ? pick(i18n.language, value.name_en, value.name_ar) : '—'}</>;
}

/** Square logo box; organisations fall back to a building, individuals to a person. */
export function BeneficiaryLogo({ url, name, kind, size = 48, className }: { url: string | null; name: string; kind: BeneficiaryKind; size?: number; className?: string }) {
  const Icon = kind === 'individual' ? User : Building2;
  return (
    <div className={cn('flex shrink-0 items-center justify-center overflow-hidden rounded-xl', kind === 'individual' ? 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-200' : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200', className)} style={{ width: size, height: size }}>
      {url ? <img src={url} alt="" loading="lazy" decoding="async" className="max-h-full max-w-full object-contain" /> : name ? <span className="text-lg font-semibold">{name.slice(0, 1)}</span> : <Icon size={size / 2} />}
    </div>
  );
}

export function KindBadge({ kind }: { kind: BeneficiaryKind }) {
  const { t } = useTranslation();
  return <Badge tone={kind === 'individual' ? 'info' : 'brand'}>{t(`beneficiaries.kinds.${kind}`)}</Badge>;
}

/** Local content score bar (MRLCO card element). */
export function ScoreBar({ value, className }: { value: number | null; className?: string }) {
  const { t, i18n } = useTranslation();
  if (value === null) return null;
  return (
    <div className={cn('text-xs', className)} title={t('beneficiaries.localContent')}>
      <div className="flex justify-between text-neutral-500"><span>{t('beneficiaries.localContentShort')}</span><span className="tabular-nums">{fmtNumber(value, i18n.language)}%</span></div>
      <div className="h-1.5 rounded-full bg-neutral-200 dark:bg-neutral-800"><div className={cn('h-full rounded-full', value >= 50 ? 'bg-emerald-500' : value >= 25 ? 'bg-amber-500' : 'bg-red-500')} style={{ width: `${Math.min(100, value)}%` }} /></div>
    </div>
  );
}

/** One impact chip: `250,000 SAR`, restricted → lock. */
export function ImpactChip({ impact }: { impact: Impact }) {
  const { i18n } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-xs tabular-nums dark:border-neutral-800 dark:bg-neutral-900" dir="auto">
      {impact.value === null ? <><Lock size={10} /> …</> : fmtNumber(impact.value, i18n.language)}
      <span className="text-neutral-500">{pick(i18n.language, impact.unit.name_en, impact.unit.name_ar)}</span>
    </span>
  );
}

export function useBeneficiaryLookups(kind?: BeneficiaryKind) {
  const orgTypes = useParameterOptions('organisation_type');
  const indTypes = useParameterOptions('individual_type');
  const sizes = useParameterOptions('company_size');
  const offices = useParameterOptions('office_type');
  const nationalities = useParameterOptions('nationality');
  const progress = useParameterOptions('enablement_progress');
  const units = useParameterOptions('impact_unit');
  const certifications = useParameterOptions('certification_name');
  const capacityUnits = useParameterOptions('capacity_unit');
  const types = kind === 'individual' ? indTypes : kind === 'organisation' ? orgTypes : { options: [...orgTypes.options, ...indTypes.options] };
  return { types, orgTypes, indTypes, sizes, offices, nationalities, progress, units, certifications, capacityUnits };
}

export function useBeneficiaryConfig() {
  return useQuery({ queryKey: ['beneficiaries', 'config'], queryFn: () => api.get<BeneficiaryConfig>('/api/beneficiaries/config'), staleTime: 60_000 });
}

export function useBeneficiarySummary(enabled = true) {
  return useQuery({ queryKey: ['beneficiaries', 'summary'], queryFn: () => api.get<BeneficiarySummary>('/api/beneficiaries/summary'), enabled, staleTime: 30_000 });
}

/** Program options for enrolment pickers: `Program — Stakeholder`. */
export function useProgramOptions(enabled = true) {
  const { i18n } = useTranslation();
  const q = useQuery({ queryKey: ['programs', 'options'], queryFn: () => api.get<ProgramList>('/api/stakeholders/programs?page_size=200&sort=stakeholder'), enabled, staleTime: 60_000 });
  const options = useMemo(() => (q.data?.items ?? []).map((p) => ({ value: p.id, label: `${pick(i18n.language, p.name_en, p.name_ar)} — ${pick(i18n.language, p.stakeholder.name_en, p.stakeholder.name_ar)}` })), [q.data, i18n.language]);
  return { options, loading: q.isLoading };
}

export function useBeneficiaryOptions(enabled = true, kind?: BeneficiaryKind) {
  const { i18n } = useTranslation();
  const q = useQuery({ queryKey: ['beneficiaries', 'options', kind ?? 'all'], queryFn: () => api.get<BeneficiaryList>(`/api/beneficiaries?page_size=200&sort=name_en${kind ? `&kind=${kind}` : ''}`), enabled, staleTime: 60_000 });
  const options = useMemo(() => (q.data?.items ?? []).map((b) => ({ value: b.id, label: pick(i18n.language, b.name_en, b.name_ar) })), [q.data, i18n.language]);
  return { options, loading: q.isLoading, items: q.data?.items ?? [] };
}

export function beneficiaryName(b: Pick<Beneficiary, 'name_en' | 'name_ar'>, locale: string): string {
  return pick(locale, b.name_en, b.name_ar);
}
